import {
  DeliveryDetailsAction,
  useDeliveryDetailsReducer,
  useGetDeliveryDetails,
} from '../hooks/deliveryDetails';
import {Dimensions, ScrollView, StyleSheet} from 'react-native';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {useIsFocused, useRoute} from '@react-navigation/native';

import {Analytics} from '@buncha/config/analytics/analytics';
import {Box} from '../../../src/components/core/view';
import ConfirmDeliveryButton from '../components/deliveryDetails/confirmDeliveryButton';
import {DeliveryDetailsContext} from '../context/deliveryDetails';
import DeliveryFailedBottomSheet from '../components/orderDetails/deliveryButtonComponent/deliveryFailedBottomSheet';
import DriverDeliveryNotes from '../components/deliveryDetails/driverDeliveryNotes';
import Header from '../../../src/components/composites/Header/Header';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '../../../src/components/conditional/If';
import KeyboardAvoidLayout from '@buncha/components/composites/keyboardAvoidLayout';
import {Modules} from '@buncha/utils/types';
import NameAndAddressDetails from '../components/common/nameAndAddressDetails';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import PhotoAndSignatureComponent from '../components/deliveryDetails/photoAndSignatureComponent';
import ScreenHeader from '../../../src/common/components/screenHeader';
import {SpinnerComponent} from '../../../src/components/core/loader';
import {Task} from '@buncha/config/onfleet/types';
import TaskCompletionCell from '../components/TaskCompletionCell';
import {isWeb} from '../../../src/utils/common';
import onfleetService from '@buncha/config/onfleet';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useColorThemeType} from '../../../src/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

type Props = NativeStackScreenProps<any, any>;

const DeliveryDetails: React.FC<Props> = props => {
  const taskId = String(props?.route?.params?.taskId) || '';
  const driverTasks = useAppSelector(gState => gState.home.driverTasks);
  const globalTask = driverTasks?.find(t => t.id === taskId);

  const theme = useColorThemeType('main');
  const [state, dispatch] = useDeliveryDetailsReducer();
  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);

  const routeParams: any = useRoute()?.params;

  const scrollViewRef = useRef<ScrollView>(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [task, setTask] = useState<Task | undefined>(globalTask);
  const orderId =
    String(props?.route?.params?.orderId ?? task?.metadata?.orderId) || '';
  const runId =
    Number(props?.route?.params?.runId ?? task?.metadata?.runId) || 0;
  const {getDeliveryDetails, loading, deliveryData} =
    useGetDeliveryDetails(dispatch);
  const buyerInfo = deliveryData?.buyer;
  const isFocused = useIsFocused();
  const deliveryLocationData = deliveryData?.deliveryLocation;
  const buyerAddress = deliveryLocationData?.userAddress;
  const deliveryLocationId = deliveryLocationData?.id || 0;
  const note = deliveryLocationData?.note || '';
  const deliveryNote = deliveryLocationData?.deliveryNote;
  const containsAlcoholicItems =
    Number(task?.minimumAge) > 0 ||
    deliveryData?.orderHasAlcoholicItems ||
    false;
  const deliveryInstruction =
    deliveryData?.deliveryInstruction ||
    buyerAddress?.deliveryInstruction ||
    '';
  const isMeijerStore = useIsMeijerStore(deliveryData?.run?.store?.id);
  const {title} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const toggleSheet = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const getDeliveryInfo = useCallback(() => {
    if (Number(orderId)) getDeliveryDetails(Number(orderId).toString(), runId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, deliveryData]);

  useEffect(() => {
    if (isFocused) getDeliveryInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    if (deliveryData && isFocused)
      Analytics.eventWithProps('Delivery Details', {
        type: 'Page',
        orderId: orderId,
        userType: 'driver',
        client: getClientType(isMeijerStore),
        storeType: getStoreName(String(deliveryData?.run?.store?.id)),
        countOfBags: deliveryData?.bagsUsed,
        countOfBarcodes: task?.barcodes,
        countOfItems: task?.quantity,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryData, isFocused]);

  const initializeDeliveryImages = useCallback(() => {
    dispatch?.(DeliveryDetailsAction.setIsImagesLoaded(true));
  }, [dispatch]);

  const selectReason = useCallback(
    (firstReason: string, secondReason: string) => {
      setReason(firstReason);
      setOtherReason(secondReason);
    },
    [],
  );

  useEffect(() => {
    if (!deliveryData) return;
    initializeDeliveryImages();
    dispatch?.(DeliveryDetailsAction.setDeliveryNote(note));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryData]);

  useEffect(() => {
    if (routeParams?.signatureUrl) {
      onfleetService.uploadSignature(
        routeParams?.signatureUrl,
        routeParams?.signatureUrl,
        routeParams?.signatureUrl,
      );
      dispatch?.(
        DeliveryDetailsAction.setSignatureImage(routeParams?.signatureUrl),
      );
      dispatch?.(DeliveryDetailsAction.setIsSignatureLoaded(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    setTimeout(() => {
      setTask(globalTask);
    }, 2000);
  }, [globalTask]);

  return (
    <KeyboardAvoidLayout>
      <Header
        loading={loading}
        headerColor={theme?.background?.blue?.[300]}
        showDefaultLeftHeader>
        <DeliveryDetailsContext.Provider value={provider}>
          <ScreenHeader title={title?.deliveryDetails} />
          <If condition={loading}>
            <SpinnerComponent containerStyle={styles.spinnerContainer} />
          </If>
          <If condition={!loading}>
            <ScrollView
              ref={scrollViewRef}
              contentContainerStyle={styles.scrollView}
              showsVerticalScrollIndicator={false}>
              <Box style={styles.parentBox}>
                <NameAndAddressDetails
                  currentTask={task}
                  buyerAddress={buyerAddress}
                  buyerInfo={buyerInfo}
                  forOrderDetails={true}
                  deliveryInstructions={deliveryInstruction}
                  orderId={Number(orderId)}
                  deliveryNote={deliveryNote}
                  runId={runId}
                  conversationId={deliveryData?.buyerChatInfo?.conversationId}
                  deliveryDetailsPage
                  bagsUsed={deliveryData?.bagsUsed}
                  storeId={deliveryData?.run?.store?.id}
                />
                <TaskCompletionCell
                  failureReason={open ? ' ' : reason}
                  toggleSheet={toggleSheet}
                  setReason={setReason}
                  otherReason={otherReason}
                />
                <If condition={!isWeb() && !reason}>
                  <PhotoAndSignatureComponent
                    deliveryLocationData={deliveryLocationData}
                    taskId={taskId}
                    orderId={orderId}
                    containsAlcoholicItems={containsAlcoholicItems}
                    runId={runId}
                    bagsUsed={deliveryData?.bagsUsed}
                    customerName={
                      task?.recipientName ||
                      `${deliveryData?.buyer?.firstName ?? ''} ${
                        deliveryData?.buyer?.lastName ?? ''
                      }`
                    }
                  />
                </If>
                <DriverDeliveryNotes
                  scrollViewRef={scrollViewRef}
                  isRequired={Boolean(task?.notesRequired)}
                />
              </Box>
            </ScrollView>
            <Box>
              <ConfirmDeliveryButton
                deliveryLocationId={deliveryLocationId}
                failureReason={reason}
                otherReason={otherReason}
                bagsUsed={deliveryData?.bagsUsed}
                orderId={orderId}
                taskId={taskId}
                isB2BStore={isMeijerStore}
              />
            </Box>
          </If>
          <DeliveryFailedBottomSheet
            open={open}
            onClose={toggleSheet}
            markOrderAsFailed={selectReason}
          />
        </DeliveryDetailsContext.Provider>
      </Header>
    </KeyboardAvoidLayout>
  );
};

export default DeliveryDetails;

const styles = StyleSheet.create({
  parentBox: {
    paddingHorizontal: 20,
    flex: 1,
    position: 'relative',
  },
  webHeader: {
    padding: 30,
  },
  spinnerContainer: {
    justifyContent: 'center',
    height: Dimensions?.get('window').height * 0.8,
  },
  scrollView: {flexGrow: 1},
});
